import { ApiError } from "@allergy123/api-client";
import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Field, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { Link } from "react-router";

/** First admin workflow (ADR 0011): organizations, their size, and product access. */
export function OrganizationsPage() {
  const api = useApi();
  const data = useLoad((a) => a.admin.organizations());
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [granting, setGranting] = useState<string | null>(null);

  const PRODUCTS = {
    learn: { label: "learning access", granted: "Learning access granted. Active members can now use training." },
    clinical: { label: "clinical access", granted: "Clinical access granted. Give each member clinical permissions on Members; nobody has any yet." },
  } as const;

  async function grant(organizationId: string, product: keyof typeof PRODUCTS) {
    setGranting(`${organizationId}:${product}`);
    setNote(null);
    try {
      await api.admin.grant({ product, subjectType: "organization", subjectId: organizationId });
      setNote(PRODUCTS[product].granted);
      data.reload();
    } catch (err) {
      setNote(`Couldn't grant ${PRODUCTS[product].label} (${err instanceof Error ? err.message : "error"}).`);
    } finally {
      setGranting(null);
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const res = await api.admin.createOrganization(name.trim());
      setNote(`Created ${res.organization.name}.`);
      setName("");
      data.reload();
    } catch (err) {
      setNote(`Couldn't create the organization (${err instanceof Error ? err.message : "error"}).`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Organizations</h1>
        <p className="max-w-[68ch] text-ink-secondary">Practices enrolled on the platform, their locations, and who belongs to them. Every change here is audited.</p>
      </div>
      {data.state === "error" &&
        (data.error instanceof ApiError && data.error.code === "mfa_required" ? (
          <Card tone="tinted" className="mb-6">
            <p className="mb-4 max-w-[60ch]">Turn on two-step verification to use the console. It takes a minute with an authenticator app.</p>
            <Button href="/security">Set up two-step verification</Button>
          </Card>
        ) : (
          <Status kind="error" label={`Couldn't load organizations (${data.error?.message})`} />
        ))}
      {note && <div className="mb-6"><Status kind="info" label={note} /></div>}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <Table caption={data.data ? `${data.data.organizations.length} organization${data.data.organizations.length === 1 ? "" : "s"}` : "Loading…"}>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Locations</Th>
                <Th>Members</Th>
                <Th>Products</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.data?.organizations.length === 0 && (
                <tr>
                  <Td colSpan={5} className="text-ink-secondary">
                    When the first practice is enrolled it appears here with its locations, members, and product access.
                  </Td>
                </tr>
              )}
              {data.data?.organizations.map((o) => (
                <tr key={o.id}>
                  <Td>
                    <div className="font-medium">{o.name}</div>
                    <div className="text-small text-ink-secondary">{o.slug}</div>
                    <div className="flex gap-3 text-small">
                      <Link to={`/organizations/${o.id}/locations`} className="text-anchor underline">Locations</Link>
                      <Link to={`/organizations/${o.id}/members`} className="text-anchor underline">Members</Link>
                      <Link to={`/organizations/${o.id}/billing`} className="text-anchor underline">Billing</Link>
                    </div>
                  </Td>
                  <Td>{o.locations}</Td>
                  <Td>{o.members}</Td>
                  <Td>
                    <div>{o.products.length ? o.products.join(", ") : <span className="text-ink-secondary">None</span>}</div>
                    {o.status === "active" && (
                      <div className="flex flex-wrap gap-2">
                        {!o.products.includes("learn") && (
                          <Button variant="secondary" surface="card" onClick={() => void grant(o.id, "learn")} disabled={granting !== null}>
                            {granting === `${o.id}:learn` ? "Granting…" : "Grant learning access"}
                          </Button>
                        )}
                        {!o.products.includes("clinical") && (
                          <Button variant="secondary" surface="card" onClick={() => void grant(o.id, "clinical")} disabled={granting !== null}>
                            {granting === `${o.id}:clinical` ? "Granting…" : "Grant clinical access"}
                          </Button>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td>{o.status}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">Add an organization</h2>
          <form onSubmit={(e) => void create(e)} className="grid gap-4">
            <Field label="Practice name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} wide hint="The address slug is derived from the name." />
            <div>
              <Button type="submit" disabled={busy || name.trim().length < 2}>
                {busy ? "Creating…" : "Create organization"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}
